/* ==========================================================================
   SLUMBERVALE — generic behaviours
   Drawer, Tabs, Carousel, Toggle, Sticky bar, Cart drawer (SV.cart),
   Cart count, SV.addToCart / SV.changeCart, Ajax product forms, Qty steppers.
   All initialisers are idempotent (guarded with data-sv-ready flags) and run
   on DOMContentLoaded and on every `shopify:section:load`.

   Cart tracking: every cart mutation goes through Shopify's Ajax Cart API
   (routes.cart_add_url → /cart/add.js, routes.cart_change_url → /cart/change.js,
   routes.cart_url → /cart.js), never a custom endpoint, so Shopify analytics
   and web pixels (product_added_to_cart, later the Facebook & Instagram app
   pixel) keep working. Checkout is the standard cart form POST (name="checkout").
   Theme-level hooks for a custom pixel (no theme change needed):
     document.addEventListener('sv:cart:added',   e => e.detail) // {items:[{id,quantity}], response, cart}
     document.addEventListener('sv:cart:updated', e => e.detail) // cart JSON (/cart.js shape)
   ========================================================================== */
(function () {
  'use strict';

  var SV = (window.SV = window.SV || {});

  SV.routes = SV.routes || {};
  var DEFAULT_ROUTES = {
    root: '/',
    cart: '/cart',
    cartAdd: '/cart/add',
    cartUpdate: '/cart/update',
    cartChange: '/cart/change'
  };
  Object.keys(DEFAULT_ROUTES).forEach(function (key) {
    if (!SV.routes[key]) SV.routes[key] = DEFAULT_ROUTES[key];
  });

  SV.strings = SV.strings || {};
  if (!SV.strings.error && SV.strings.cartError) SV.strings.error = SV.strings.cartError;
  var DEFAULT_STRINGS = {
    addToCart: 'Add to Cart',
    soldOut: 'Sold Out',
    adding: 'Adding…',
    added: 'Added to cart',
    error: 'Something went wrong. Please try again.'
  };
  Object.keys(DEFAULT_STRINGS).forEach(function (key) {
    if (!SV.strings[key]) SV.strings[key] = DEFAULT_STRINGS[key];
  });
  if (!SV.strings.cartError) SV.strings.cartError = SV.strings.error;

  var READY = 'svReady';

  function once(el, key) {
    var flag = READY + key;
    if (el.dataset[flag] === 'true') return false;
    el.dataset[flag] = 'true';
    return true;
  }

  function byId(id) {
    if (!id) return null;
    return document.getElementById(id);
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.getClientRects().length > 0;
    });
  }

  /* --------------------------------------------------------------------------
     Drawer
     [data-sv-drawer-open="ID"] opens #ID, [data-sv-drawer-close] closes,
     Escape closes, backdrop click closes. Body scroll is locked while open,
     focus is trapped inside and the closed drawer is `inert`.
     -------------------------------------------------------------------------- */
  var Drawer = {
    current: null,
    lastFocus: null,

    open: function (drawer, opener) {
      if (!drawer) return;
      if (Drawer.current && Drawer.current !== drawer) Drawer.close(Drawer.current);
      Drawer.lastFocus = opener || document.activeElement;
      drawer.removeAttribute('inert');
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sv-no-scroll');
      Drawer.current = drawer;
      document.querySelectorAll('[data-sv-drawer-open="' + drawer.id + '"]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'true');
      });
      var focusTarget = drawer.querySelector('[data-sv-drawer-close], [data-sv-cart-close], a, button, input, select, textarea');
      if (focusTarget) {
        window.setTimeout(function () {
          focusTarget.focus();
        }, 50);
      }
      dispatch('sv:drawer:open', { drawer: drawer });
    },

    close: function (drawer) {
      drawer = drawer || Drawer.current;
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      document.querySelectorAll('[data-sv-drawer-open="' + drawer.id + '"]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      if (Drawer.current === drawer) Drawer.current = null;
      if (!document.querySelector('.sv-drawer.is-open')) {
        document.body.classList.remove('sv-no-scroll');
      }
      if (Drawer.lastFocus && typeof Drawer.lastFocus.focus === 'function' && document.body.contains(Drawer.lastFocus)) {
        Drawer.lastFocus.focus();
      }
      Drawer.lastFocus = null;
      dispatch('sv:drawer:close', { drawer: drawer });
    },

    init: function (root) {
      root.querySelectorAll('[data-sv-drawer-open]').forEach(function (btn) {
        if (!once(btn, 'DrawerOpen')) return;
        var id = btn.getAttribute('data-sv-drawer-open');
        btn.setAttribute('aria-controls', id);
        if (!btn.hasAttribute('aria-expanded')) btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          Drawer.open(byId(id), btn);
        });
      });

      root.querySelectorAll('.sv-drawer, [data-sv-drawer]').forEach(function (drawer) {
        if (!once(drawer, 'Drawer')) return;
        if (!drawer.classList.contains('is-open')) {
          drawer.setAttribute('aria-hidden', 'true');
          drawer.setAttribute('inert', '');
        }
        drawer.querySelectorAll('[data-sv-drawer-close]').forEach(function (btn) {
          btn.addEventListener('click', function (event) {
            event.preventDefault();
            Drawer.close(drawer);
          });
        });
        var backdrop = drawer.querySelector('.sv-drawer__backdrop');
        if (backdrop) {
          backdrop.addEventListener('click', function () {
            Drawer.close(drawer);
          });
        }
      });
    }
  };

  document.addEventListener('keydown', function (event) {
    var drawer = Drawer.current;
    if (!drawer) return;
    if (event.key === 'Escape') {
      Drawer.close(drawer);
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables(drawer);
    if (!items.length) {
      event.preventDefault();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    var inside = drawer.contains(active);
    if (event.shiftKey && (active === first || !inside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault();
      first.focus();
    }
  });

  /* --------------------------------------------------------------------------
     Tabs
     [data-sv-tabs] container; [data-sv-tab="key"] buttons; [data-sv-panel="key"]
     panels. EVERY element sharing the same panel key is toggled together, so a
     section may split one "panel" into several DOM nodes (text + media).
     -------------------------------------------------------------------------- */
  var Tabs = {
    activate: function (container, key, focusTab) {
      var tabs = container.querySelectorAll('[data-sv-tab]');
      var panels = container.querySelectorAll('[data-sv-panel]');
      var activeTab = null;

      tabs.forEach(function (tab) {
        var isActive = tab.getAttribute('data-sv-tab') === key;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive) activeTab = tab;
      });

      panels.forEach(function (panel) {
        var isActive = panel.getAttribute('data-sv-panel') === key;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      container.setAttribute('data-sv-tabs-active', key);
      if (focusTab && activeTab) activeTab.focus();
      dispatch('sv:tabs:change', { container: container, key: key });
    },

    init: function (root) {
      root.querySelectorAll('[data-sv-tabs]').forEach(function (container) {
        if (!once(container, 'Tabs')) return;
        var tabs = Array.prototype.slice.call(container.querySelectorAll('[data-sv-tab]'));
        if (!tabs.length) return;

        tabs.forEach(function (tab) {
          var key = tab.getAttribute('data-sv-tab');
          tab.setAttribute('role', 'tab');
          if (tab.tagName === 'BUTTON' && !tab.getAttribute('type')) tab.setAttribute('type', 'button');
          var firstPanel = container.querySelector('[data-sv-panel="' + key + '"]');
          if (firstPanel && firstPanel.id && !tab.getAttribute('aria-controls')) {
            tab.setAttribute('aria-controls', firstPanel.id);
          }
          tab.addEventListener('click', function (event) {
            event.preventDefault();
            Tabs.activate(container, key, false);
          });
          tab.addEventListener('keydown', function (event) {
            var idx = tabs.indexOf(tab);
            var next = null;
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = tabs[(idx + 1) % tabs.length];
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = tabs[(idx - 1 + tabs.length) % tabs.length];
            if (event.key === 'Home') next = tabs[0];
            if (event.key === 'End') next = tabs[tabs.length - 1];
            if (next) {
              event.preventDefault();
              Tabs.activate(container, next.getAttribute('data-sv-tab'), true);
            }
          });
        });

        container.querySelectorAll('[data-sv-panel]').forEach(function (panel) {
          panel.setAttribute('role', 'tabpanel');
        });

        var tablist = container.querySelector('[data-sv-tablist]');
        if (tablist) tablist.setAttribute('role', 'tablist');

        var initial = container.getAttribute('data-sv-tabs-active');
        var preselected = tabs.filter(function (t) {
          return t.classList.contains('is-active') || t.getAttribute('aria-selected') === 'true';
        })[0];
        var startKey = initial || (preselected ? preselected.getAttribute('data-sv-tab') : tabs[0].getAttribute('data-sv-tab'));
        Tabs.activate(container, startKey, false);
      });
    }
  };

  /* --------------------------------------------------------------------------
     Carousel
     [data-sv-carousel] holds a .sv-scroller plus [data-sv-carousel-prev] and
     [data-sv-carousel-next]; buttons scroll one item (+gap) and are disabled
     at the ends.
     -------------------------------------------------------------------------- */
  var Carousel = {
    step: function (scroller) {
      var item = scroller.querySelector('.sv-scroller__item') || scroller.firstElementChild;
      if (!item) return scroller.clientWidth;
      var styles = window.getComputedStyle(scroller);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return item.getBoundingClientRect().width + gap;
    },

    update: function (container, scroller, prev, next) {
      var max = scroller.scrollWidth - scroller.clientWidth;
      var pos = scroller.scrollLeft;
      var atStart = pos <= 2;
      var atEnd = pos >= max - 2;
      if (prev) {
        prev.disabled = atStart;
        prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      }
      if (next) {
        next.disabled = atEnd;
        next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }
      container.classList.toggle('is-at-start', atStart);
      container.classList.toggle('is-at-end', atEnd);
    },

    init: function (root) {
      root.querySelectorAll('[data-sv-carousel]').forEach(function (container) {
        if (!once(container, 'Carousel')) return;
        var scroller = container.querySelector('.sv-scroller');
        if (!scroller) return;
        var prev = container.querySelector('[data-sv-carousel-prev]');
        var next = container.querySelector('[data-sv-carousel-next]');
        var ticking = false;

        function refresh() {
          Carousel.update(container, scroller, prev, next);
        }

        if (prev) {
          prev.addEventListener('click', function (event) {
            event.preventDefault();
            scroller.scrollBy({ left: -Carousel.step(scroller), behavior: 'smooth' });
          });
        }
        if (next) {
          next.addEventListener('click', function (event) {
            event.preventDefault();
            scroller.scrollBy({ left: Carousel.step(scroller), behavior: 'smooth' });
          });
        }

        scroller.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            refresh();
            ticking = false;
          });
        }, { passive: true });

        window.addEventListener('resize', refresh);
        if ('ResizeObserver' in window) {
          new ResizeObserver(refresh).observe(scroller);
        }
        refresh();
        window.setTimeout(refresh, 250);
      });
    }
  };

  /* --------------------------------------------------------------------------
     Toggle
     [data-sv-toggle="ID"] toggles `hidden` on #ID and aria-expanded on the
     button; optional data-sv-toggle-label-open / -close swap the label.
     -------------------------------------------------------------------------- */
  var Toggle = {
    set: function (btn, target, expanded) {
      if (expanded) {
        target.removeAttribute('hidden');
      } else {
        target.setAttribute('hidden', '');
      }
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      btn.classList.toggle('is-open', expanded);
      target.classList.toggle('is-open', expanded);

      var labelOpen = btn.getAttribute('data-sv-toggle-label-open');
      var labelClose = btn.getAttribute('data-sv-toggle-label-close');
      if (labelOpen || labelClose) {
        var labelEl = btn.querySelector('[data-sv-toggle-label]') || btn;
        var text = expanded ? labelClose : labelOpen;
        if (text) labelEl.textContent = text;
      }

      var hideWhenOpen = btn.getAttribute('data-sv-toggle-hide') === 'true';
      if (hideWhenOpen && expanded) btn.setAttribute('hidden', '');

      dispatch('sv:toggle', { button: btn, target: target, expanded: expanded });
    },

    init: function (root) {
      root.querySelectorAll('[data-sv-toggle]').forEach(function (btn) {
        if (!once(btn, 'Toggle')) return;
        var id = btn.getAttribute('data-sv-toggle');
        var target = byId(id);
        if (!target) return;
        btn.setAttribute('aria-controls', id);
        if (btn.tagName === 'BUTTON' && !btn.getAttribute('type')) btn.setAttribute('type', 'button');
        var expanded = btn.getAttribute('aria-expanded') === 'true' && !target.hasAttribute('hidden');
        Toggle.set(btn, target, expanded);
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          var isExpanded = btn.getAttribute('aria-expanded') === 'true';
          Toggle.set(btn, target, !isExpanded);
        });
      });
    }
  };

  /* --------------------------------------------------------------------------
     Sticky bar
     [data-sv-sticky] + data-sv-sticky-trigger="ID": the bar becomes visible once
     #ID has scrolled fully above the viewport, hidden while the trigger is in
     view, and hidden while the footer (.sv-footer) is >= 50% in view.
     -------------------------------------------------------------------------- */
  var Sticky = {
    init: function (root) {
      root.querySelectorAll('[data-sv-sticky]').forEach(function (bar) {
        if (!once(bar, 'Sticky')) return;
        var triggerId = bar.getAttribute('data-sv-sticky-trigger');
        var trigger = byId(triggerId);
        var footer = document.querySelector('.sv-footer');
        var state = { triggerAbove: false, footerVisible: false };

        function apply() {
          var visible = state.triggerAbove && !state.footerVisible;
          bar.classList.toggle('is-visible', visible);
          bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
          document.documentElement.style.setProperty('--sv-sticky-height', visible ? bar.offsetHeight + 'px' : '0px');
        }

        if (!trigger || !('IntersectionObserver' in window)) {
          apply();
          return;
        }

        var triggerObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            state.triggerAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          });
          apply();
        }, { root: null, rootMargin: '0px', threshold: 0 });
        triggerObserver.observe(trigger);

        if (footer) {
          var footerObserver = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              state.footerVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
            });
            apply();
          }, { root: null, rootMargin: '0px', threshold: [0, 0.5, 1] });
          footerObserver.observe(footer);
        }

        apply();
      });
    }
  };

  /* --------------------------------------------------------------------------
     Cart count — listens for `sv:cart:updated` and updates [data-sv-cart-count]
     -------------------------------------------------------------------------- */
  function renderCartCount(cart) {
    if (!cart) return;
    var count = typeof cart.item_count === 'number' ? cart.item_count : parseInt(cart.item_count, 10) || 0;
    document.querySelectorAll('[data-sv-cart-count]').forEach(function (el) {
      el.textContent = String(count);
      if (count > 0) {
        el.removeAttribute('hidden');
        el.classList.add('is-visible');
      } else {
        el.setAttribute('hidden', '');
        el.classList.remove('is-visible');
      }
    });
  }

  document.addEventListener('sv:cart:updated', function (event) {
    renderCartCount(event.detail);
  });

  /* --------------------------------------------------------------------------
     Ajax Cart API helpers (Shopify endpoints from routes, `.js` appended)
     -------------------------------------------------------------------------- */
  function jsonRoute(route) {
    if (!route) return route;
    return /\.js$/.test(route) ? route : route + '.js';
  }

  function readJson(res) {
    return res.text().then(function (text) {
      var data = null;
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (err) {
          data = null;
        }
      }
      if (!res.ok || (data && typeof data === 'object' && data.status)) {
        var error = new Error((data && (data.description || data.message)) || SV.strings.error);
        error.status = res.status;
        error.data = data;
        throw error;
      }
      return data || {};
    });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(readJson);
  }

  /* --------------------------------------------------------------------------
     Cart drawer — SV.cart
     Root #sv-cart-drawer (section cart-drawer, always rendered by theme.liquid),
     [data-sv-cart-panel], [data-sv-cart-close], [data-sv-cart-overlay],
     [data-sv-cart-content] (swapped with the Section Rendering API output),
     [data-sv-cart-change] quantity buttons (data-line / data-quantity),
     [data-sv-cart-open] triggers (header cart icon), [data-sv-cart-error].
     Enabled when <body data-cart-type="drawer">; every method is a no-op when
     the root is absent (cart type "page").
     -------------------------------------------------------------------------- */
  var Cart = {
    SECTION: 'cart-drawer',
    ROOT_ID: 'sv-cart-drawer',
    pending: false,
    editorSelected: false,

    root: function () {
      return document.getElementById(Cart.ROOT_ID);
    },

    type: function () {
      var type = document.body ? document.body.getAttribute('data-cart-type') : null;
      return type || 'drawer';
    },

    enabled: function () {
      return Cart.type() === 'drawer' && !!Cart.root();
    },

    isOpen: function () {
      var root = Cart.root();
      return !!(root && root.classList.contains('is-open'));
    },

    sectionsUrl: function () {
      return window.location.pathname;
    },

    open: function (opener) {
      var root = Cart.root();
      if (!root) return;
      Cart.clearError();
      Drawer.open(root, opener);
      document.body.classList.add('sv-cart-open');
      dispatch('sv:cart:open', { root: root });
    },

    close: function () {
      var root = Cart.root();
      if (!root) return;
      Drawer.close(root);
    },

    /* GET <path>?sections=cart-drawer and swap the content */
    refresh: function () {
      if (!Cart.root()) return Promise.resolve(false);
      return fetch(Cart.sectionsUrl() + '?sections=' + Cart.SECTION, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(readJson)
        .then(function (sections) {
          return Cart.replaceFromSections(sections);
        });
    },

    /* `sections` = the `sections` object of an Ajax API response (or of the
       ?sections= GET). Only [data-sv-cart-content] is replaced, so the open
       state of the root is preserved. */
    replaceFromSections: function (sections) {
      var root = Cart.root();
      if (!root || !sections) return false;
      var html = sections[Cart.SECTION];
      if (typeof html !== 'string' || !html) return false;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-sv-cart-content]');
      var target = root.querySelector('[data-sv-cart-content]');
      if (!fresh || !target) return false;

      var hadFocusInside = root.contains(document.activeElement);
      target.innerHTML = fresh.innerHTML;
      target.className = fresh.className;
      var items = fresh.getAttribute('data-sv-cart-items');
      if (items !== null) {
        target.setAttribute('data-sv-cart-items', items);
        renderCartCount({ item_count: parseInt(items, 10) || 0 });
      }
      initAll(target);

      if (root.classList.contains('is-open') && hadFocusInside && !root.contains(document.activeElement)) {
        var closeBtn = root.querySelector('[data-sv-cart-close]');
        if (closeBtn) closeBtn.focus();
      }
      dispatch('sv:cart:rendered', { root: root, content: target });
      return true;
    },

    applySections: function (data) {
      if (data && data.sections) return Cart.replaceFromSections(data.sections);
      return false;
    },

    busy: function (state) {
      Cart.pending = !!state;
      var root = Cart.root();
      if (!root) return;
      root.classList.toggle('is-busy', Cart.pending);
      root.querySelectorAll('[data-sv-cart-qty]').forEach(function (group) {
        if (Cart.pending) {
          group.setAttribute('aria-busy', 'true');
        } else {
          group.removeAttribute('aria-busy');
        }
        group.querySelectorAll('button').forEach(function (btn) {
          btn.disabled = Cart.pending;
        });
      });
    },

    showError: function (message) {
      var root = Cart.root();
      if (!root) return;
      var box = root.querySelector('[data-sv-cart-error]');
      if (!box) return;
      // Reveal first, then populate: a live region that changes while
      // display:none is not reliably announced by screen readers.
      box.removeAttribute('hidden');
      window.requestAnimationFrame(function () {
        box.textContent = message || SV.strings.error;
        // The box is the last child of the scrolling body: bring it into view
        // when the failed request came from a stepper near the top.
        if (typeof box.scrollIntoView === 'function') {
          var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          box.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
        }
      });
    },

    clearError: function () {
      var root = Cart.root();
      if (!root) return;
      root.querySelectorAll('[data-sv-cart-error]').forEach(function (box) {
        box.textContent = '';
        box.setAttribute('hidden', '');
      });
    },

    /* POST /cart/change.js {line, quantity, sections} → swap + sv:cart:updated */
    change: function (line, quantity) {
      if (Cart.pending) return Promise.resolve(null);
      Cart.clearError();
      Cart.busy(true);
      return SV.changeCart({ line: line, quantity: quantity })
        .then(function (cart) {
          Cart.busy(false);
          return cart;
        })
        .catch(function (err) {
          Cart.busy(false);
          Cart.showError(err && err.message);
          throw err;
        });
    },

    init: function (root) {
      var drawer = Cart.root();
      if (drawer && once(drawer, 'Cart') && !drawer.classList.contains('is-open')) {
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
      }
      root.querySelectorAll('[data-sv-cart-open]').forEach(function (el) {
        if (!once(el, 'CartOpen')) return;
        if (Cart.enabled()) {
          el.setAttribute('aria-controls', Cart.ROOT_ID);
          el.setAttribute('aria-haspopup', 'dialog');
        }
      });
    }
  };

  /* Delegated clicks: open triggers, close/overlay, quantity buttons */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var root = Cart.root();

    var changeBtn = target.closest('[data-sv-cart-change]');
    if (changeBtn && root && root.contains(changeBtn)) {
      event.preventDefault();
      if (changeBtn.disabled || Cart.pending) return;
      var line = parseInt(changeBtn.getAttribute('data-line'), 10);
      var quantity = parseInt(changeBtn.getAttribute('data-quantity'), 10);
      if (isNaN(line) || isNaN(quantity)) return;
      var group = changeBtn.closest('[data-sv-cart-qty]');
      var index = group ? Array.prototype.indexOf.call(group.querySelectorAll('[data-sv-cart-change]'), changeBtn) : -1;
      Cart.change(line, quantity)
        .then(function () {
          // Keep keyboard focus on the equivalent button of the re-rendered line
          var fresh = root.querySelector('[data-sv-cart-line="' + line + '"]');
          var buttons = fresh ? fresh.querySelectorAll('[data-sv-cart-change]') : [];
          var next = buttons[index] || buttons[buttons.length - 1] || root.querySelector('[data-sv-cart-close]');
          if (next && root.classList.contains('is-open')) next.focus();
        })
        .catch(function () {});
      return;
    }

    var closer = target.closest('[data-sv-cart-close], [data-sv-cart-overlay]');
    if (closer && root && root.contains(closer)) {
      event.preventDefault();
      Cart.close();
      return;
    }

    var opener = target.closest('[data-sv-cart-open]');
    if (opener) {
      if (!Cart.enabled()) return; // cart type "page": normal navigation to /cart
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      opener.setAttribute('aria-busy', 'true');
      var done = function () {
        opener.removeAttribute('aria-busy');
        Cart.open(opener);
      };
      Cart.refresh().then(done, done);
    }
  });

  document.addEventListener('sv:drawer:close', function (event) {
    var drawer = event.detail && event.detail.drawer;
    if (drawer && drawer.id === Cart.ROOT_ID) {
      document.body.classList.remove('sv-cart-open');
      dispatch('sv:cart:close', { root: drawer });
    }
  });

  /* --------------------------------------------------------------------------
     SV.addToCart(items, options) → POST /cart/add.js with
     { items, sections: ['cart-drawer'], sections_url }, swap the drawer content,
     open the drawer (cart type "drawer"), then GET /cart.js and dispatch
     `sv:cart:updated` (cart) + `sv:cart:added` ({items, response, cart}).
     Resolves with the cart object (or with the /cart/add.js response when the
     follow-up GET /cart.js fails: the add itself succeeded); rejects with
     Error{status, data} on 422.
     options: { open: false } keeps the drawer closed, { opener } = element to
     return focus to when the drawer closes.
     -------------------------------------------------------------------------- */
  SV.fetchCart = function () {
    return fetch(jsonRoute(SV.routes.cart), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(readJson);
  };

  SV.addToCart = function (items, options) {
    options = options || {};
    var list = Array.isArray(items) ? items : [items];
    list = list
      .filter(function (item) {
        return item && item.id;
      })
      .map(function (item) {
        return {
          id: parseInt(item.id, 10) || item.id,
          quantity: parseInt(item.quantity, 10) || 1,
          properties: item.properties || undefined,
          selling_plan: item.selling_plan || undefined
        };
      });

    if (!list.length) {
      return Promise.reject(new Error('No items to add'));
    }

    var body = { items: list };
    if (Cart.root()) {
      body.sections = [Cart.SECTION];
      body.sections_url = Cart.sectionsUrl();
    }

    var response = null;
    return postJson(jsonRoute(SV.routes.cartAdd), body)
      .then(function (data) {
        response = data;
        if (!Cart.enabled()) return data;
        if (Cart.applySections(data)) return data;
        // The API did not return the section: re-render it before opening
        return Cart.refresh().then(function () {
          return data;
        }, function () {
          return data;
        });
      })
      .then(function () {
        if (Cart.enabled() && options.open !== false) Cart.open(options.opener);
        // The item IS in the cart at this point: a failure of the follow-up
        // GET /cart.js must not turn a successful add into an error (the header
        // count was already refreshed from the re-rendered section).
        return SV.fetchCart().catch(function () {
          return null;
        });
      })
      .then(function (cart) {
        if (cart) dispatch('sv:cart:updated', cart);
        dispatch('sv:cart:added', { items: list, response: response, cart: cart });
        return cart || response;
      })
      .catch(function (err) {
        if (Cart.isOpen()) Cart.showError(err && err.message);
        throw err;
      });
  };

  /* SV.changeCart({line|id, quantity, properties…}) → POST /cart/change.js,
     swap the drawer content when the response carries `sections`,
     dispatch `sv:cart:updated`; resolves with the cart object. */
  SV.changeCart = function (payload) {
    var body = {};
    Object.keys(payload || {}).forEach(function (key) {
      body[key] = payload[key];
    });
    if (!body.sections && Cart.root()) {
      body.sections = [Cart.SECTION];
      body.sections_url = Cart.sectionsUrl();
    }
    return postJson(jsonRoute(SV.routes.cartChange), body).then(function (cart) {
      Cart.applySections(cart);
      dispatch('sv:cart:updated', cart);
      dispatch('sv:cart:changed', { request: payload, cart: cart });
      return cart;
    });
  };

  /* --------------------------------------------------------------------------
     Ajax product forms: forms with [data-sv-ajax-form] (and every
     form[action*="/cart/add"] inside .sv-product) submit through SV.addToCart.
     The markup stays a real <form method="post" action="/cart/add"> with
     name="id" / name="quantity" inputs, so apps, pixels and no-JS keep working.
     data-sv-ajax-form="cart": open the cart drawer when enabled, else go to the
     cart page (previous behaviour). Any other value: stay on the page.
     Buttons submitting the form through a `form="…"` attribute (sticky bar)
     are covered because they fire the same submit event.
     -------------------------------------------------------------------------- */
  var AjaxForm = {
    init: function (root) {
      root.querySelectorAll('form[data-sv-ajax-form], .sv-product form[action*="/cart/add"]').forEach(function (form) {
        if (!once(form, 'AjaxForm')) return;
        form.addEventListener('submit', function (event) {
          var idInput = form.querySelector('[name="id"]');
          if (!idInput || !idInput.value || typeof window.fetch !== 'function') return; // native submit
          event.preventDefault();
          // Re-entrancy guard: one request (plus its "Added to cart" flash) at a time
          if (form.dataset.svPending === 'true') return;
          form.dataset.svPending = 'true';

          var qtyInput = form.querySelector('[name="quantity"]');
          var buttons = Array.prototype.slice.call(form.querySelectorAll('[type="submit"]'));
          // External submitters (sticky bar) point at the form with form="…".
          // Read the id ATTRIBUTE: on a <form>, `form.id` is shadowed by the
          // control named "id" (the hidden variant input), not the id string.
          var formId = form.getAttribute('id');
          if (formId) {
            document.querySelectorAll('button[form="' + formId + '"]').forEach(function (b) {
              if (buttons.indexOf(b) === -1) buttons.push(b);
            });
          }
          if (event.submitter && buttons.indexOf(event.submitter) === -1) buttons.push(event.submitter);
          var originals = buttons.map(function (b) {
            return b.textContent;
          });
          var wasEnabled = buttons.map(function (b) {
            return !b.disabled;
          });
          var errorBox = form.querySelector('[data-sv-form-error]');
          if (errorBox) {
            errorBox.textContent = '';
            errorBox.setAttribute('hidden', '');
          }
          buttons.forEach(function (b, i) {
            b.classList.add('is-loading');
            b.setAttribute('aria-busy', 'true');
            if (wasEnabled[i]) b.disabled = true; // sold-out buttons are left as they are
            if (SV.strings.adding) b.textContent = SV.strings.adding;
          });

          var mode = form.getAttribute('data-sv-ajax-form') || 'cart';
          var opener = event.submitter || buttons[0] || null;
          var flash = false;
          var failed = false;
          var redirecting = false;

          SV.addToCart([{ id: idInput.value, quantity: qtyInput ? qtyInput.value : 1 }], { opener: opener })
            .then(function () {
              if (mode === 'cart' && !Cart.enabled()) {
                redirecting = true; // keep the buttons locked while navigating away
                window.location.href = SV.routes.cart;
                return;
              }
              if (SV.strings.added) {
                flash = true;
                buttons.forEach(function (b) {
                  b.textContent = SV.strings.added;
                });
              }
            })
            .catch(function (err) {
              failed = true;
              var message = (err && err.message) || SV.strings.error;
              if (errorBox) {
                errorBox.removeAttribute('hidden');
                window.requestAnimationFrame(function () {
                  errorBox.textContent = message;
                });
              } else {
                window.alert(message);
              }
            })
            .then(function () {
              if (redirecting) return;
              buttons.forEach(function (b, i) {
                b.classList.remove('is-loading');
                b.removeAttribute('aria-busy');
                if (wasEnabled[i]) b.disabled = false;
              });
              // Disabling the focused submitter drops focus in some browsers; on a
              // failed add (drawer not opened) hand it back to that button.
              if (failed && opener && typeof opener.focus === 'function' && document.activeElement === document.body) {
                opener.focus();
              }
              var restore = function () {
                buttons.forEach(function (b, i) {
                  // The section renders the canonical label (data-label-add /
                  // data-label-soldout); the snapshot is only a fallback.
                  var label = b.getAttribute('data-label-add');
                  if (b.disabled && b.getAttribute('data-label-soldout')) label = b.getAttribute('data-label-soldout');
                  b.textContent = label || originals[i];
                });
                delete form.dataset.svPending;
              };
              if (flash) {
                window.setTimeout(restore, 1500);
              } else {
                restore();
              }
            });
        });
      });
    }
  };

  /* --------------------------------------------------------------------------
     Quantity steppers: .sv-qty with [data-sv-qty-minus] / [data-sv-qty-plus]
     -------------------------------------------------------------------------- */
  var Qty = {
    init: function (root) {
      root.querySelectorAll('.sv-qty, [data-sv-qty]').forEach(function (wrap) {
        if (!once(wrap, 'Qty')) return;
        var input = wrap.querySelector('input[type="number"], input');
        if (!input) return;
        function change(delta) {
          var min = parseInt(input.min, 10);
          var max = parseInt(input.max, 10);
          var value = (parseInt(input.value, 10) || 0) + delta;
          if (!isNaN(min)) value = Math.max(min, value);
          if (!isNaN(max)) value = Math.min(max, value);
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        var minus = wrap.querySelector('[data-sv-qty-minus]');
        var plus = wrap.querySelector('[data-sv-qty-plus]');
        if (minus) minus.addEventListener('click', function (e) { e.preventDefault(); change(-1); });
        if (plus) plus.addEventListener('click', function (e) { e.preventDefault(); change(1); });
      });
    }
  };

  /* --------------------------------------------------------------------------
     Init
     -------------------------------------------------------------------------- */
  function initAll(root) {
    root = root && root.querySelectorAll ? root : document;
    Drawer.init(root);
    Tabs.init(root);
    Carousel.init(root);
    Toggle.init(root);
    Sticky.init(root);
    Cart.init(root);
    AjaxForm.init(root);
    Qty.init(root);
    dispatch('sv:init', { root: root });
  }

  SV.initAll = initAll;
  SV.Drawer = Drawer;
  SV.Tabs = Tabs;
  SV.Carousel = Carousel;
  SV.Toggle = Toggle;
  SV.Sticky = Sticky;
  SV.cart = Cart;
  SV.Cart = Cart;
  SV.renderCartCount = renderCartCount;

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function holdsCartDrawer(el) {
    return !!(el && el.querySelector && el.querySelector('#' + Cart.ROOT_ID));
  }

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target || document;
    initAll(target);
    if (holdsCartDrawer(target) && Cart.editorSelected) Cart.open();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (target && target.querySelector && target.querySelector('.sv-drawer.is-open')) {
      document.body.classList.remove('sv-no-scroll');
      Drawer.current = null;
    }
    if (holdsCartDrawer(target)) document.body.classList.remove('sv-cart-open');
  });

  /* Theme editor: preview the drawer while its section is selected */
  document.addEventListener('shopify:section:select', function (event) {
    if (!holdsCartDrawer(event.target)) return;
    Cart.editorSelected = true;
    Cart.open();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    if (!holdsCartDrawer(event.target)) return;
    Cart.editorSelected = false;
    Cart.close();
  });
})();
